import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronDown, Download, Settings as SettingsIcon, Upload } from "lucide-react";
import type { ImportResult } from "@desk/contracts";
import { Button } from "@/components/ui/button";
import { Input, Kbd } from "@/components/ui/input";
import { Segmented, Select, Switch } from "@/components/ui/misc";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { useArchived, useHierarchy, useSettings } from "@/data/hooks";
import { useDesk } from "@/data/context";
import { describeError } from "@/data/api";
import { invalidateContainers } from "@/data/cache";
import { useConnection } from "@/data/connection";
import { useSaveSettings } from "@/data/settings-save";
import { CONCEPT_ORDER, CONCEPTS } from "@/concepts";
import type { Appearance } from "@/concepts/types";
import { useConcept } from "@/features/concept-provider";
import { useDesktopPrefs } from "@/features/desktop/useDesktopPrefs";
import { DestIcon, DestinationPicker, destLabel, savedDest } from "@/features/destination/DestinationPicker";
import { chipClass } from "@/features/quick-add/QuickAdd";
import { shortcutKeys } from "@/features/keyboard/shortcuts";
import { requestNotificationSync } from "@/features/notifications/useWebNotifications";
import { ui } from "@/features/ui-state";
import { DesktopSection } from "@/features/settings/DesktopSection";
import { CloudSection, SharingSection } from "@/features/settings/CloudSection";
import { McpSection } from "@/features/settings/McpSection";
import { NumberField, Row, Section } from "@/features/settings/parts";
import { notificationDeviceId, useStored } from "@/lib/storage";
import { DEFAULTS, PREF, type CaptureDefault, type OpenTo, type TimeFormat, type WeekStart } from "@/lib/prefs";
import { downloadJson, maskToken, modKey } from "@/lib/utils";
import { useCurrentDestination } from "./ContainerScreens";

function ImportDialog({ file, onClose }: { file: { name: string; data: unknown } | null; onClose: () => void }) {
  const { api } = useDesk();
  const qc = useQueryClient();
  const [summary, setSummary] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ran, setRan] = useState<string | null>(null);
  if (file && ran !== file.name) {
    setRan(file.name);
    setSummary(null);
    setError(null);
    api
      .importData(file.data, true)
      .then((s) => setSummary(s as Record<string, unknown>))
      .catch((e) => setError(describeError(e)));
  }
  const confirm = async () => {
    if (!file) return;
    setBusy(true);
    try {
      await api.importData(file.data, false);
      await invalidateContainers(qc);
      toast("Import merged");
      onClose();
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  };
  const r = summary as unknown as ImportResult | null;
  const KEYS = ["areas", "spaces", "projects", "tasks", "planEntries"] as const;
  const LABEL: Record<(typeof KEYS)[number], string> = { areas: "Areas", spaces: "Spaces", projects: "Projects", tasks: "Tasks", planEntries: "Plan entries" };
  return (
    <Dialog open={!!file} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[440px]">
        <DialogBody>
          <DialogTitle>Import {file?.name}</DialogTitle>
          <DialogDescription>Merge only adds records that don't exist yet. Nothing existing is overwritten or deleted.</DialogDescription>
          {error && <p role="alert" className="rounded-md bg-urgent-soft px-3 py-2 text-[13px]">{error}</p>}
          {!summary && !error && <p className="text-[13px] text-muted-foreground">Checking the file…</p>}
          {r?.created && (
            <div className="rounded-md bg-muted px-3 py-2 text-[13px]">
              <div className="grid grid-cols-[1fr_auto_auto] gap-x-6 gap-y-1">
                <span />
                <span className="text-right text-muted-foreground">Add</span>
                <span className="text-right text-muted-foreground">Already here</span>
                {KEYS.map((k) => (
                  <div key={k} className="contents">
                    <span>{LABEL[k]}</span>
                    <span className="text-right tabular">{r.created[k]}</span>
                    <span className="text-right tabular text-muted-foreground">{r.skipped[k]}</span>
                  </div>
                ))}
              </div>
              {(r.renumbered.length > 0 || r.reprefixed.length > 0) && (
                <p className="mt-2 text-[12.5px] text-muted-foreground">
                  {r.renumbered.length > 0 && `${r.renumbered.length} task IDs are taken and will be renumbered. `}
                  {r.reprefixed.length > 0 && `${r.reprefixed.length} prefixes are taken and will change.`}
                </p>
              )}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="default" disabled={!summary || busy} onClick={() => void confirm()}>
            Merge into Desk
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ArchivedList() {
  const { api } = useDesk();
  const qc = useQueryClient();
  const { data } = useArchived();
  const restore = async (kind: "area" | "space" | "project", id: string, name: string) => {
    try {
      await api.containerAction(kind, id, "restore");
      await invalidateContainers(qc);
      toast(`Restored ${name}`);
    } catch (e) {
      toast.error("Couldn't restore", { description: describeError(e) });
    }
  };
  const items = [
    ...(data?.areas ?? []).map((a) => ({ kind: "area" as const, id: a.id, name: a.name })),
    ...(data?.spaces ?? []).map((s) => ({ kind: "space" as const, id: s.id, name: s.name })),
    ...(data?.projects ?? []).map((p) => ({ kind: "project" as const, id: p.id, name: p.name })),
  ];
  if (!items.length) return <p className="text-[13.5px] text-muted-foreground">Nothing archived.</p>;
  return (
    <ul className="flex flex-col">
      {items.map((i) => (
        <li key={i.id} className="flex h-9 items-center gap-3 rounded-md px-1 hover:bg-hover">
          <span className="min-w-0 flex-1 truncate text-[14px]">{i.name}</span>
          <span className="text-[12.5px] capitalize text-subtle">{i.kind}</span>
          <Button size="xs" variant="outline" onClick={() => void restore(i.kind, i.id, i.name)}>
            Restore
          </Button>
        </li>
      ))}
    </ul>
  );
}

/** Readable names for notification device ids ("desktop", "web-1a2b3c4d", "ios-…"). */
function deviceLabel(id: string, thisDevice: string): string {
  const desktop = id === "desktop";
  if (id === thisDevice) return desktop ? "This Mac" : "This browser";
  if (desktop) return "Desk for Mac";
  if (id.startsWith("ios-")) return `iPhone (${id})`;
  if (id.startsWith("web-")) return `A browser (${id})`;
  return id;
}

/** Settings → New tasks go to: Inbox, the last destination used, or one pinned list. */
function CaptureDefaultRow() {
  const h = useHierarchy();
  const [pref, setPref] = useStored<CaptureDefault>(PREF.captureDefault, DEFAULTS.captureDefault);
  const pinned = pref.kind === "pinned" ? savedDest(pref.dest, h) : null;
  // The pinned list was archived or deleted: quick add falls back to Inbox.
  const lost = pref.kind === "pinned" && h.loaded && !!(pref.dest.areaId || pref.dest.spaceId) && !pinned!.areaId && !pinned!.spaceId;
  return (
    <Row
      label="New tasks go to"
      error={lost}
      hint={lost ? "That list is no longer available, so new tasks go to Inbox." : "When quick add opens outside a list."}
    >
      {pref.kind === "pinned" && (
        <DestinationPicker value={pinned!} onChange={(dest) => setPref({ kind: "pinned", dest })} align="end">
          <button type="button" className={chipClass} aria-label="Pinned list">
            <DestIcon d={pinned!} />
            <span className="truncate">{destLabel(pinned!, h)}</span>
            <ChevronDown className="opacity-50" />
          </button>
        </DestinationPicker>
      )}
      <Select
        ariaLabel="New tasks go to"
        value={pref.kind}
        onValueChange={(v) =>
          setPref(v === "pinned" ? { kind: "pinned", dest: pref.kind === "pinned" ? pref.dest : { areaId: null, spaceId: null, projectId: null } } : { kind: v as "inbox" | "last" })
        }
        options={[
          { value: "inbox", label: "Inbox" },
          { value: "last", label: "Last used" },
          { value: "pinned", label: "A pinned list" },
        ]}
        className="w-[150px]"
      />
    </Row>
  );
}

export function SettingsScreen() {
  const { concept, setConcept, appearance, setAppearance } = useConcept();
  const { api, session, signOut } = useDesk();
  const live = useConnection();
  const { data: settings } = useSettings();
  const save = useSaveSettings();
  const [desktop, updateDesktop] = useDesktopPrefs();
  const [openTo, setOpenTo] = useStored<OpenTo>(PREF.openTo, DEFAULTS.openTo);
  const [weekStart, setWeekStart] = useStored<WeekStart>(PREF.weekStart, DEFAULTS.weekStart);
  const [timeFormat, setTimeFormat] = useStored<TimeFormat>(PREF.timeFormat, DEFAULTS.timeFormat);
  const fileRef = useRef<HTMLInputElement>(null);
  const [importFile, setImportFile] = useState<{ name: string; data: unknown } | null>(null);
  const [perm, setPerm] = useState(() => (typeof Notification === "undefined" ? "unsupported" : Notification.permission));
  const thisDevice = notificationDeviceId();
  useCurrentDestination(null);

  const fu = settings?.followUp;
  const qh = settings?.quietHours;
  const kinds = settings?.notificationKinds;
  const pref = settings?.preferredNotificationDevice ?? null;
  const deviceOptions = [
    { value: "any", label: "Any device" },
    { value: thisDevice, label: deviceLabel(thisDevice, thisDevice) },
    ...(pref && pref !== thisDevice ? [{ value: pref, label: deviceLabel(pref, thisDevice) }] : []),
  ];
  const otherPreferred = !!pref && pref !== thisDevice;
  const version = desktop?.version ?? __DESK_VERSION__;

  return (
    <>
      <concept.PageHeader title="Settings" icon={<SettingsIcon />} />
      <div className="max-w-[640px]">
        <Section title="Appearance">
          <Row label="Theme">
            <Segmented<Appearance>
              ariaLabel="Theme"
              value={appearance}
              onChange={setAppearance}
              options={[
                { value: "system", label: "System" },
                { value: "light", label: "Light" },
                { value: "dark", label: "Dark" },
              ]}
            />
          </Row>
          <Row label="Week starts on">
            <Segmented<WeekStart>
              ariaLabel="Week starts on"
              value={weekStart}
              onChange={setWeekStart}
              options={[
                { value: "system", label: "System" },
                { value: "sunday", label: "Sunday" },
                { value: "monday", label: "Monday" },
              ]}
            />
          </Row>
          <Row label="Time format">
            <Segmented<TimeFormat>
              ariaLabel="Time format"
              value={timeFormat}
              onChange={setTimeFormat}
              options={[
                { value: "system", label: "System" },
                { value: "12h", label: "12-hour" },
                { value: "24h", label: "24-hour" },
              ]}
            />
          </Row>
        </Section>

        <Section title="General">
          <Row label="Open to">
            <Segmented<OpenTo>
              ariaLabel="Open to"
              value={openTo}
              onChange={setOpenTo}
              options={[
                { value: "today", label: "Today" },
                { value: "inbox", label: "Inbox" },
                { value: "last", label: "Last view" },
              ]}
            />
          </Row>
          <CaptureDefaultRow />
          <Row label="Keyboard shortcuts">
            <Kbd className="h-[22px] min-w-[22px] text-[12px]">{shortcutKeys("shortcuts")}</Kbd>
            <Button size="sm" variant="outline" onClick={() => ui.set({ shortcutsOpen: true })}>
              Show
            </Button>
          </Row>
        </Section>

        <Section title="Follow-up">
          {!settings ? (
            <p className="text-[13.5px] text-muted-foreground">Loading…</p>
          ) : (
            <>
              <Row label="Surface stale tasks in Review">
                <Switch checked={fu!.enabled} onCheckedChange={(v) => void save({ followUp: { enabled: v } })} aria-label="Follow-up enabled" />
              </Row>
              <Row label="Notify once for undated urgent tasks" hint={`At ${String(fu!.notifyHour).padStart(2, "0")}:00 the next morning. Other priorities never notify.`}>
                <Switch checked={fu!.urgentNotify} onCheckedChange={(v) => void save({ followUp: { urgentNotify: v } })} aria-label="Urgent notification" />
              </Row>
              <Row label="High priority after">
                <NumberField label="High days" value={fu!.highDays} min={1} max={90} suffix="days" onCommit={(n) => void save({ followUp: { highDays: n } })} />
              </Row>
              <Row label="Medium priority after">
                <NumberField label="Medium days" value={fu!.mediumDays} min={1} max={90} suffix="days" onCommit={(n) => void save({ followUp: { mediumDays: n } })} />
              </Row>
              <Row label="Low and no priority after">
                <NumberField label="Low days" value={fu!.lowDays} min={1} max={180} suffix="days" onCommit={(n) => void save({ followUp: { lowDays: n } })} />
              </Row>
              <Row label="Follow-up hour">
                <NumberField label="Notify hour" value={fu!.notifyHour} min={0} max={23} suffix=":00" onCommit={(n) => void save({ followUp: { notifyHour: n } })} />
              </Row>
              <Row label="Quiet hours" hint={qh!.enabled ? "Notifications that fall inside are held until it ends." : undefined}>
                {qh!.enabled && (
                  <>
                    <Input aria-label="Quiet hours start" type="time" className="w-[124px]" value={qh!.start} onChange={(e) => void save({ quietHours: { start: e.target.value } })} />
                    <span className="text-muted-foreground">–</span>
                    <Input aria-label="Quiet hours end" type="time" className="w-[124px]" value={qh!.end} onChange={(e) => void save({ quietHours: { end: e.target.value } })} />
                  </>
                )}
                <Switch checked={qh!.enabled} onCheckedChange={(v) => void save({ quietHours: { enabled: v } })} aria-label="Quiet hours enabled" />
              </Row>
              <Row label="Timezone">
                <span className="text-[13.5px] text-muted-foreground">{settings.timezone}</span>
              </Row>
            </>
          )}
        </Section>

        <Section title="Notifications">
          <Row label="Preferred device" hint="Reminders show on this device to avoid duplicates. Every device still shows the same state.">
            <Select
              ariaLabel="Preferred notification device"
              value={pref ?? "any"}
              onValueChange={(v) => void save({ preferredNotificationDevice: v === "any" ? null : v })}
              options={deviceOptions}
              className="w-[220px]"
            />
          </Row>
          {kinds && (
            <>
              <Row label="Deadline notices" hint={`At ${String(fu!.notifyHour).padStart(2, "0")}:00 on the due date.`}>
                <Switch checked={kinds.deadline} onCheckedChange={(v) => void save({ notificationKinds: { deadline: v } })} aria-label="Deadline notices" />
              </Row>
              <Row label="Snooze wake-ups" hint="Reminders you set always notify.">
                <Switch checked={kinds.snoozeWake} onCheckedChange={(v) => void save({ notificationKinds: { snoozeWake: v } })} aria-label="Snooze wake-ups" />
              </Row>
            </>
          )}
          {window.deskBridge ? (
            desktop && (
              <Row
                label="Show notifications on this Mac"
                hint={
                  !desktop.notificationsSupported
                    ? "Not supported on this system."
                    : desktop.notificationsEnabled && otherPreferred
                      ? `${deviceLabel(pref!, thisDevice)} is the preferred device, so this Mac stays quiet.`
                      : "While Desk is running. macOS asks for permission the first time."
                }
              >
                <Switch
                  checked={desktop.notificationsEnabled}
                  disabled={!desktop.notificationsSupported}
                  onCheckedChange={(v) => void updateDesktop({ notificationsEnabled: v }).catch((e: Error) => toast.error("Couldn't change notifications", { description: e.message }))}
                  aria-label="Show notifications on this Mac"
                />
              </Row>
            )
          ) : (
            <Row label="Browser notifications" hint={otherPreferred && perm === "granted" ? `${deviceLabel(pref!, thisDevice)} is the preferred device, so this tab stays quiet.` : "Only while this tab is open."}>
              {perm === "granted" ? (
                <span className="text-[13px] text-muted-foreground">Allowed</span>
              ) : perm === "denied" ? (
                <span className="text-[13px] text-muted-foreground">Blocked in browser settings</span>
              ) : perm === "unsupported" ? (
                <span className="text-[13px] text-muted-foreground">Not supported</span>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    void Notification.requestPermission().then((p) => {
                      setPerm(p);
                      requestNotificationSync();
                    })
                  }
                >
                  Allow notifications
                </Button>
              )}
            </Row>
          )}
        </Section>

        {desktop && <DesktopSection prefs={desktop} update={updateDesktop} />}

        <CloudSection />
        <SharingSection />

        {session.source !== "cloud" && <McpSection />}

        <Section title="Data">
          <Row label="Export" hint="Everything as JSON, including completed and deleted tasks.">
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                void api
                  .exportData()
                  .then((d) => downloadJson(`desk-export-${new Date().toISOString().slice(0, 10)}.json`, d))
                  .catch((e) => toast.error("Export failed", { description: describeError(e) }))
              }
            >
              <Download />
              Export JSON
            </Button>
          </Row>
          <Row label="Import" hint="Shows what would be added before anything changes.">
            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (!f) return;
                try {
                  setImportFile({ name: f.name, data: JSON.parse(await f.text()) });
                } catch {
                  toast.error("That file isn't valid JSON");
                }
              }}
            />
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
              <Upload />
              Choose file…
            </Button>
          </Row>
        </Section>

        <Section title="Archived">
          <ArchivedList />
        </Section>

        <Section title="Connection">
          <Row label="Service">
            <span className="text-[13px] tabular text-muted-foreground">{session.apiUrl}</span>
          </Row>
          {session.source !== "cloud" && (
            <Row label="Token">
              <span className="font-mono text-[12.5px] text-muted-foreground">{maskToken(session.token)}</span>
            </Row>
          )}
          <Row label="Status">
            <span className="text-[13px] text-muted-foreground">
              {live.live === "live" ? "Live updates on" : live.live === "connecting" ? "Connecting…" : "Offline — changes are kept on this device"}
            </span>
          </Row>
          {session.source !== "cloud" && (
            <Row label="Paired by">
              <span className="text-[13px] text-muted-foreground">
                {session.source === "electron" ? "Desk desktop app" : session.source === "dev" ? "Local development server" : "Manual pairing"}
              </span>
              {session.source === "paired" && (
                <Button size="sm" variant="outline" onClick={signOut}>
                  Unpair
                </Button>
              )}
            </Row>
          )}
        </Section>

        <Section title="Design concept (preview)">
          <Segmented
            ariaLabel="Design concept"
            className="self-start"
            value={concept.id}
            onChange={setConcept}
            options={CONCEPT_ORDER.map((id, i) => ({ value: id, label: CONCEPTS[id].name, title: `${CONCEPTS[id].name} (${modKey}⇧${i + 1})` }))}
          />
        </Section>

        <Section title="About Desk">
          <p className="text-[14px]">
            A product of{" "}
            <a href="https://www.aevixtech.com" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:text-muted-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4">
              Aevix Technologies
            </a>
          </p>
          <Row label="Version">
            <span className="text-[13px] tabular text-muted-foreground">
              {version}
              {__DESK_BUILD__ && ` (${__DESK_BUILD__})`}
            </span>
          </Row>
          <p className="text-[12.5px] text-muted-foreground">© 2026 Aevix Technologies. All rights reserved.</p>
        </Section>
      </div>
      <ImportDialog file={importFile} onClose={() => setImportFile(null)} />
    </>
  );
}
